import { collectEvents } from 'clients/MonitorClient';
import { TOGGLE_FEATURE_PREFIX } from 'constants/ToggleFeature';
import Cookies from 'js-cookie';
import { useLayoutEffect } from 'react';
import { GENERAL_DOMAIN } from 'sysconfig';
import { setCacheVersion } from 'utils/localStorageUtils';
import create from 'zustand';
import createContext from 'zustand/context';

export const LICENSE_OPTIONS = [
  {
    value: 'business',
    configKey: 'licenses',
    accepts: ['BUSINESS'],
    label: 'Giấy phép kinh doanh',
    stepName: 'Tải lên giấy phép kinh doanh',
    action: false,
  },
  {
    value: 'gpp',
    configKey: 'gpp',
    accepts: ['GPP', 'GDP', 'GSP'],
    label: 'Hồ sơ GPP',
    stepName: 'Tải lên hồ sơ GPP',
    action: false,
  },
  {
    value: 'pharmacyEligibility',
    configKey: 'pharmacyEligibilityLicense',
    accepts: ['PHARMACY_ELIGIBILITY'],
    label: 'Giấy chứng nhận đủ điều kiện kinh doanh dược',
    stepName: 'Tải lên GCN đủ điều kiện kinh doanh dược',
    action: false,
  },
  {
    value: 'gdp',
    configKey: 'gdp',
    accepts: ['GPP', 'GDP', 'GSP'],
    label: 'Hồ sơ GDP',
    stepName: 'Tải lên hồ sơ GDP',
    action: false,
  },
  {
    value: 'examinationAndTreatment',
    configKey: 'examinationAndTreatmentLicense',
    accepts: ['EXAMINATION_AND_TREATMENT'],
    label: 'Giấy phép hoạt động khám bệnh - chữa bệnh',
    stepName: 'Tải lên giấy phép hoạt động khám bệnh - chữa bệnh',
    action: false,
  },
  // GSP and the practising certificate are configurable but have no step here:
  // this wizard posts to the v1 OCR endpoint, which routes five document slots
  // only. They are collected on /my-account, which posts to v2 and classifies
  // the page itself.
];

// Reading the matrix from the settings API replaced a build-time env var. The
// env var is still honoured while the two deploys are out of step, and will go
// once the API has been serving it for a while.
//
// The parse is guarded: the old code called JSON.parse on the raw value during
// render, and the default was an empty string, so any environment that did not
// inject it crashed the business-information tab instead of falling back.

let store;

const getDefaultInitialState = () => ({
  lastUpdate: Date.now(),
  tabs: [],
  banners: [],
  campaigns: [],
  tags: [],
  provinces: [],
  sellers: [],
  user: {},
  logs: false,
  toggleFeatures: {},
  timeLogs: 0,
  timeInitStoreGlobal: +new Date(),
  isMienBac: null,
  eventCollectors: [],
  appSetting: {},
  customerSetting: {}, // customer config
  licenseConfig: {}, // scope -> {required, optional} licences (BV-1670)
  replaceHangDiemSetting: {},
});

export const { Provider, useStore } = createContext();

const PhonenumberUseMobileV2 = [];

function initializeStore(preloadedState = {}) {
  return create((set, get) => ({
    //
    sendEventCollector: async (event) => {
      const eventData = await event;
      get()?.eventCollectors?.push(eventData);
    },
    logout: () => {
      set('user', null);
    },
    ...getDefaultInitialState(),
    ...preloadedState,
    canUseMobileV2: () => {
      const { user } = get();
      return user && PhonenumberUseMobileV2?.indexOf(user?.phone) >= 0;
    },
    timeInitStoreGlobal: +new Date(),

    getCustomer: () => get()?.user, // lấy customer
    getAccount: () => get()?.user?.account, // lấy account
    getSession: () => get()?.user?.account, // lấy session

    // hostname
    getHostName: () => (window && window?.location?.hostname) || '',
    getStyleBySlugOfTag: (code) =>
      get()
        ?.tags?.filter((item) => item.visible)
        ?.find((item) => item.code === code),
    getTagBySlug: (slug) => get()?.tags?.find((item) => item.slug === slug),
    getSellerByCode: (code) => get()?.sellers?.find((seller) => seller.code === code) || {},
    toggleFeature: (featureName, value = null, { isCookie = false }) => {
      // console.log('🚀 ~ file: storeGlobal.js ~ line 51 ~ create ~ featureName', featureName, value, isCookie);
      if (isCookie)
        Cookies.set(featureName, value, {
          domain: GENERAL_DOMAIN,
          sameSite: 'Lax',
        });
    },
    validateFeature: (featureName = '', options = {}) => {
      const { isCookie = false } = options || {};
      // console.log(
      //   '🚀 ~ file: storeGlobal.js ~ line 58 ~ create ~ featureName',
      //   featureName,
      //   isCookie,
      //   typeof Cookies.get(featureName),
      //   Cookies.get(featureName) === 'true',
      // );
      if (isCookie) {
        return Cookies.get(featureName) === 'true';
      }
      const toggleFeatures = get()?.toggleFeatures || {};
      // on / off tính năng với user : bằng customerId / accountId /phone
      // get config .env
      // TOGGLE_FEATURE_CHAT_ENABLE = on / off
      // TOGGLE_FEATURE_CHAT_TYPE = proviceCode / accountID / customerID / phone
      // TOGGLE_FEATURE_CHAT_VALUES = ['00','123','0974860956']
      const enable = toggleFeatures[`${TOGGLE_FEATURE_PREFIX + featureName}_ENABLE`] === 'on';
      // if feature not enable in env => false
      if (enable) {
        const values = toggleFeatures[`${TOGGLE_FEATURE_PREFIX}${featureName}_VALUES`];
        // values contains 00 -> all
        if (values.indexOf('00') >= 0) {
          return true;
        }
        const type = toggleFeatures[`${TOGGLE_FEATURE_PREFIX}${featureName}_TYPE`];
        const { user } = get();
        switch (type) {
          case 'provinceCode':
            return values.indexOf(`${user?.provinceCode}`) >= 0;
          case 'accountID':
            return values.indexOf(`${user?.accountID}`) >= 0;
          case 'customerID':
            return values.indexOf(`${user?.customerID}`) >= 0;
          case 'phone':
            return values.indexOf(`${user?.phone}`) >= 0;
          case 'scope':
            return values.indexOf(`${user?.scope}`) >= 0;
          // phone
          default:
            return true;
        }
      }
      return false;
    },
    // Which licences this customer's kind of business must send, and which it
    // may send. Empty means ask for nothing at all and show no upload boxes.
    getLicenseConfigByScope: () => {
      // Không có cấu hình cho loại hình này thì không hỏi giấy nào. Trước đây rơi về
      // NEXT_PUBLIC_OCR_CONFIG_LIST, mà biến đó không phân biệt bắt buộc với tuỳ
      // chọn nên biến mọi giấy thành bắt buộc -- sai lặng lẽ, khó lần ra.
      const { licenseConfig, user } = get();
      const scopeConfig = licenseConfig?.[user?.scope];
      const required = scopeConfig?.required || [];
      const optional = scopeConfig?.optional || [];
      return { required, optional, all: [...required, ...optional] };
    },
    // The steps of the upload wizard, in the order operations configured them.
    getLicenseOptionsByScope: () => {
      const { all, required } = get().getLicenseConfigByScope();
      return all
        .map((configKey) => LICENSE_OPTIONS.find((option) => option.configKey === configKey))
        .filter(Boolean)
        .map((option) => ({ ...option, isRequired: required.includes(option.configKey) }));
    },
  }));
}

export function useCreateStore(serverInitialState) {
  // Server side code: For SSR & SSG, always use a new store.
  if (typeof window === 'undefined') {
    return () => initializeStore(serverInitialState);
  }
  // End of server side code

  // Client side code:
  // Next.js always re-uses same store regardless of whether page is a SSR or SSG or CSR type.
  const isReusingStore = Boolean(store);
  store = store ?? initializeStore(serverInitialState);

  // When next.js re-renders _app while re-using an older store, then replace current state with
  // the new state (in the next render cycle).
  // (Why next render cycle? Because react cannot re-render while a render is already in progress.
  // i.e. we cannot do a setState() as that will initiate a re-render)
  //
  // eslint complaining "React Hooks must be called in the exact same order in every component render"
  // is ignorable as this code runs in same order in a given environment (i.e. client or server)

  let interval = null;

  useLayoutEffect(() => {
    // use CACHE_VERSION from app setting https://internal.v2-stg.thuocsi.vn/config/service-admin/app/config?code=884FW961
    // Change it to invalidate cache
    setCacheVersion(serverInitialState.appSetting?.CACHE_VERSION);

    // serverInitialState is undefined for CSR pages. It is up to you if you want to reset
    // states on CSR page navigation or not. I have chosen not to, but if you choose to,
    // then add `serverInitialState = getDefaultInitialState()` here.
    if (serverInitialState && isReusingStore) {
      store.setState(
        {
          // re-use functions from existing store
          ...store.getState(),
          // but reset all other properties.
          ...serverInitialState,
          timeInitStoreGlobal: +new Date(),
          timeInitStoreGlobalInitialState: +new Date(),
          interval,
        },
        true, // replace states, rather than shallow merging
      );
    } else {
      // interval counter  event
      if (!store?.setState()?.internval) {
        interval = setInterval(() => {
          const { eventCollectors } = store.getState();
          if (eventCollectors.length === 0) return;

          collectEvents(eventCollectors);
          store.setState({ eventCollectors: [] });
        }, 2000);
      }
    }
  });

  return () => store;
}
